import React, { useState } from "react";
import { Button, Table, Progress, Alert } from "reactstrap";
import * as XLSX from "xlsx";
import { useTenant } from "../../../hooks/useTenant";
import { ContentLoader, Breadcrumb } from "../../../components/Common";
import { toast } from "react-toastify";
import MemberService from "../../../services/member_service";

const ImportMembersPage = () => {
    document.title = "Importar Alunos | NeoApp - Sistema de Gestão";

    const { idTenant, user } = useTenant();
    const [file, setFile] = useState(null);
    const [previewData, setPreviewData] = useState([]);
    const [isImporting, setIsImporting] = useState(false);
    const [progress, setProgress] = useState(0);
    const [stats, setStats] = useState(null);

    const downloadTemplate = () => {
        const ws = XLSX.utils.json_to_sheet([
            {
                Nome: "Exemplo",
                Sobrenome: "Aluno da Silva",
                "E-mail": "aluno@exemplo.com.br",
                "N de Telefone": "(11) 99999-9999",
                CPF: "123.456.789-00",
            }
        ]);
        // Ajusta largura visual das colunas do modelo
        ws['!cols'] = [{ wpx: 120 }, { wpx: 180 }, { wpx: 200 }, { wpx: 120 }, { wpx: 120 }];

        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Modelo");
        XLSX.writeFile(wb, "Modelo_Importacao_Alunos.xlsx");
    };

    // Validação de formato ou CPF etc poderia ser feita durante a montagem
    // Basic rules to map standard Excel header names to Member fields
    const mapColumn = (row, possibleNames) => {
        const key = Object.keys(row).find(k =>
            possibleNames.some(name => k.toLowerCase().trim().includes(name.toLowerCase()))
        );
        return key ? (row[key] || "").toString().trim() : "";
    };

    const handleFileUpload = (e) => {
        const selectedFile = e.target.files[0];
        if (!selectedFile) return;

        setFile(selectedFile);
        setProgress(0);
        setStats(null);

        const reader = new FileReader();
        reader.onload = (evt) => {
            try {
                const bstr = evt.target.result;
                const wb = XLSX.read(bstr, { type: "binary" });
                const wsname = wb.SheetNames[0];
                const ws = wb.Sheets[wsname];
                const data = XLSX.utils.sheet_to_json(ws);

                if (data.length === 0) {
                    toast.error("A planilha parece estar vazia.");
                    return;
                }

                // Formatter para nomes (Maiúsculas/minúsculas)
                const formatName = (name) => {
                    if (!name) return "";
                    return name.trim().split(/\s+/).map((word, index) => {
                        const lower = word.toLowerCase();
                        if (index > 0 && ['da', 'de', 'do', 'das', 'dos', 'e'].includes(lower)) {
                            return lower;
                        }
                        return lower.charAt(0).toUpperCase() + lower.slice(1);
                    }).join(' ');
                };

                // Normalizing data to standard Member properties
                const normalized = data.map((row) => {
                    const rawFirstName = mapColumn(row, ["nome", "first name", "aluno"]);
                    const rawLastName = mapColumn(row, ["sobrenome", "last name"]);

                    let firstName = "";
                    let lastName = "";

                    if (rawLastName) {
                        firstName = rawFirstName;
                        lastName = rawLastName;
                    } else if (rawFirstName) {
                        const parts = rawFirstName.split(" ");
                        firstName = parts[0] || "";
                        lastName = parts.slice(1).join(" ") || "";
                    }

                    firstName = formatName(firstName);
                    lastName = formatName(lastName);
                    const fullName = `${firstName} ${lastName}`.trim();

                    const emailRaw = mapColumn(row, ["email", "e-mail"]);
                    const email = emailRaw ? emailRaw.toLowerCase().trim() : "";

                    return {
                        firstName,
                        lastName,
                        fullName,
                        email,
                        phone: mapColumn(row, ["telefone", "n de telefone", "celular", "whatsapp", "phone"]),
                        document: mapColumn(row, ["cpf", "document", "documento"]),
                        status: "lead", // Padrão de entrada
                        raw: row // Pra debug se necessário
                    };
                }).filter(m => m.firstName); // ignora linhas em branco sem nome

                setPreviewData(normalized);
                toast.success(`${normalized.length} registros identificados!`);
            } catch (err) {
                console.error(err);
                toast.error("Erro ao ler o arquivo. Certifique-se de que é um Excel ou CSV válido.");
            }
        };
        reader.readAsBinaryString(selectedFile);
    };

    const handleImport = async () => {
        if (!idTenant || previewData.length === 0) return;

        setIsImporting(true);
        let successCount = 0;
        let errorCount = 0;

        try {
            // Em vez de salvar de 1 em 1 por fora e arriscar colisão de idMember (transações encavaladas),
            // Vamos iterar um a um com o serviço padrão que já tem 'runTransaction'.
            // (Para lotes na casa de centenas, a persistência individual de transação enfileirada no loop é segura, 
            // embora levemente mais lenta devido ao round-trip).
            for (let i = 0; i < previewData.length; i++) {
                const member = previewData[i];
                try {
                    await MemberService.save(member, idTenant, user, []);
                    successCount++;
                } catch (error) {
                    console.error("Erro ao importar registro", member, error);
                    errorCount++;
                }

                setProgress(Math.round(((i + 1) / previewData.length) * 100));
            }

            toast.success("Importação concluída com sucesso!");
            setFile(null);
            setPreviewData([]);
        } catch (globalError) {
            console.error("Erro generalizado na importação:", globalError);
            toast.error("Processo interrompido por um erro Crítico.");
        } finally {
            setStats({ success: successCount, errors: errorCount, total: previewData.length });
            setIsImporting(false);
            setProgress(100);
        }
    };

    const cancelImport = () => {
        setFile(null);
        setPreviewData([]);
        setProgress(0);
        setStats(null);
    };

    return (
        <React.Fragment>
            <div className="d-flex align-items-center justify-content-between mb-3">
                <Breadcrumb title="Importar Alunos" />
            </div>

            <div className="bg-white p-4 rounded-0 border-top">
                {stats && (
                    <Alert color={stats.errors > 0 ? "warning" : "success"} className="mb-4 rounded-0 border-0 bg-light" fade={false}>
                        <i className="mdi mdi-information-outline me-2"></i>
                        Importação finalizada! <strong>{stats.success}</strong> adicionados com sucesso.
                        {stats.errors > 0 && <span> ({stats.errors} falhas)</span>}
                    </Alert>
                )}

                {!file && (
                    <div className="text-center py-5 border rounded-0 bg-light" style={{ borderStyle: 'dashed' }}>
                        <i className="mdi mdi-cloud-upload-outline display-4 text-primary mb-3"></i>
                        <h5 className="fw-semibold text-dark">Arraste a planilha ou clique para buscar</h5>
                        <p className="text-muted mb-4 font-size-13">Suporta arquivos .xlsx e .csv. Limite recomendado de 500 registros por vez.</p>
                        <div>
                            <input
                                type="file"
                                accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                                id="upload-excel"
                                className="d-none"
                                onChange={handleFileUpload}
                            />
                            <div className="d-flex justify-content-center gap-3">
                                <label htmlFor="upload-excel" className="btn btn-primary px-4 mb-0 rounded-0 fw-bold">
                                    Selecionar Arquivo
                                </label>
                                <Button color="light" className="px-4 text-primary rounded-0 fw-bold border" onClick={downloadTemplate}>
                                    <i className="mdi mdi-download-outline me-2"></i> Baixar Modelo
                                </Button>
                            </div>
                        </div>
                    </div>
                )}

                {previewData.length > 0 && !isImporting && progress === 0 && (
                    <div>
                        <div className="d-flex align-items-center justify-content-between mb-3 border-bottom pb-4 mt-2">
                            <div>
                                <h5 className="fw-bold mb-1">Pré-visualização dos Dados</h5>
                                <span className="text-muted font-size-13">{previewData.length} registros prontos para entrar no sistema.</span>
                            </div>
                            <div className="d-flex gap-2">
                                <Button color="light" className="rounded-0 border px-3" onClick={cancelImport}>
                                    Cancelar
                                </Button>
                                <Button color="success" className="px-4 d-flex align-items-center rounded-0 fw-bold" onClick={handleImport}>
                                    <i className="mdi mdi-database-import-outline me-2"></i> Iniciar Importação
                                </Button>
                            </div>
                        </div>

                        <div className="table-responsive" style={{ maxHeight: "400px" }}>
                            <Table className="table mb-0 table-hover align-middle">
                                <thead className="table-light sticky-top">
                                    <tr className="border-top-0">
                                        <th className="border-top-0">#</th>
                                        <th className="border-top-0">Nome do Aluno</th>
                                        <th className="border-top-0">Aviso (E-mail)</th>
                                        <th className="border-top-0">Aviso (Telefone)</th>
                                        <th className="border-top-0">Documento / CPF</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {previewData.slice(0, 100).map((member, index) => (
                                        <tr key={index}>
                                            <td className="text-muted" style={{ width: "40px" }}>{index + 1}</td>
                                            <td className="fw-semibold text-dark">{member.fullName}</td>
                                            <td>{member.email || <span className="text-warning small fst-italic">Sem e-mail</span>}</td>
                                            <td>{member.phone || <span className="text-warning small fst-italic">Sem telefone</span>}</td>
                                            <td>{member.document || <span className="text-muted small fst-italic">-</span>}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>
                        {previewData.length > 100 && (
                            <div className="text-center text-muted small mt-3">
                                Exibindo apenas os 100 primeiros registros por questões de desempenho de tela.
                            </div>
                        )}
                    </div>
                )}

                {isImporting && (
                    <div className="text-center py-5">
                        <ContentLoader />
                        <h5 className="mt-4 mb-2 fw-semibold text-dark">Inserindo registros no Banco de Dados...</h5>
                        <p className="text-muted mb-4 font-size-13">Não feche ou recarregue esta página.</p>

                        <div style={{ maxWidth: "400px", margin: "0 auto" }}>
                            <Progress value={progress} color="success" className="mb-2 rounded-0" style={{ height: "10px" }} />
                            <div className="d-flex justify-content-between text-muted font-size-12 fw-semibold">
                                <span>{progress}%</span>
                                <span>{Math.round((progress / 100) * previewData.length)} / {previewData.length} alunos</span>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </React.Fragment>
    );

};

export default ImportMembersPage;
